import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Typography,
  CircularProgress,
  Grid,
  Alert,
  Stack,
  DialogActions,
  Link,
  DialogTitle,
  DialogContent,
  Dialog,
  Button,
  AlertTitle,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { AdminUITheme } from 'types/AdminUiThemeMui'
import { useAlert } from 'components/Shared/Alert'
import { getBaseURL } from 'lib/utility/GetBaseUrl'
import importUsersAPI from 'api/Participant/ImportUsers/Action'
import { isFrenchLocale } from 'lib/utility/LocaleHelper'
import {
  BLANK_CSV,
  getImportUsersHelpDeskURL,
  handleFileUpload,
  PARTIAL_IMPORT,
} from 'components/Participants/AddParticipants/Helper'
import { useAppDispatch } from 'redux/Hook'
import { ParticpantsContext } from 'containers/Participants'

type AddDialogProps = {
  isOpen: boolean
  handleCancel: () => void
}

const importUsersDialog = ({ isOpen, handleCancel }: AddDialogProps) => {
  const locale = isFrenchLocale() ? 'fr' : 'en'
  const DEFAULT_TEMPLATE_URL = `${getBaseURL()}/api/v2/participant_imports/template`
  const defaultAlertMetaData = {
    title: '',
    subTitle: '',
    error: '',
    templateURL: DEFAULT_TEMPLATE_URL,
  }
  const { t } = useTranslation()
  const [importing, setImporting] = React.useState(false)
  const [alertMetaData, setAlertMetaData] = React.useState(defaultAlertMetaData)
  const { showAlert } = useAlert()
  const dispatch = useAppDispatch()
  const { onClearFilter } = React.useContext(ParticpantsContext).functions
  const { searchInputRef } = React.useContext(ParticpantsContext).state

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setImporting(true)
    const response = await handleFileUpload(
      e,
      importUsersAPI,
      showAlert,
      handleCancel,
      getBaseURL,
      t,
      dispatch,
      onClearFilter,
      searchInputRef,
    )
    setAlertMetaData(response)
    setImporting(false)
  }
  const theme: AdminUITheme = useTheme()
  const HelpArticleURL = getImportUsersHelpDeskURL(locale)
  const { title, subTitle, error, templateURL } = alertMetaData
  return (
    <Dialog
      fullScreen
      open={isOpen}
      aria-labelledby={t(`participant.inviteAdmin.inviteOption`)}
      aria-describedby={t(`participant.inviteAdmin.descriptionText`)}
      data-testid="invite-dialog"
      PaperProps={{ sx: theme.ParticipantGrid.importUsers.paperCss }}
      sx={theme.ParticipantGrid.importUsers.dialog}
    >
      <DialogTitle sx={theme.GenericTheme.zeropaddingAndMargin}>
        <Typography variant="h4" component="h4" pt={3}>
          {t(`participant.importUsers.title`)}
        </Typography>
      </DialogTitle>

      <DialogContent sx={theme.GenericTheme.zeropaddingAndMargin}>
        <Grid container>
          <Grid item xs={12} md={7}>
            <Stack sx={{ width: '100%' }}>
              {!error && (
                <Alert
                  severity="warning"
                  sx={theme.ParticipantGrid.importUsers.alert1MB}
                >
                  {t(`participant.importUsers.alert1MB`)}
                </Alert>
              )}
              {error && error === BLANK_CSV && (
                <Alert
                  severity="error"
                  sx={{
                    marginTop: '1rem',
                  }}
                >
                  {title}
                </Alert>
              )}
              {error && title && subTitle && (
                <Alert
                  severity="error"
                  sx={theme.ParticipantGrid.importUsers.alert1MB}
                >
                  <AlertTitle>{title}</AlertTitle>
                  {subTitle}
                </Alert>
              )}
            </Stack>
            <Button
              variant="outlined"
              size="large"
              sx={theme.ParticipantGrid.importUsers.downloadCSV}
            >
              <Link href={templateURL} underline="none">
                {error && error === PARTIAL_IMPORT
                  ? t(`participant.importUsers.downloadErrorCSV`)
                  : t(`participant.importUsers.downloadCSV`)}
              </Link>
            </Button>
            <Typography
              variant="body1"
              pb={3}
              sx={theme.ParticipantGrid.importUsers.firstPara}
            >
              {t(`participant.importUsers.para1`)}
            </Typography>
            <Typography variant="body1" pb={3}>
              {t(`participant.importUsers.para2`)}
            </Typography>
            <Typography variant="body1" pb={3}>
              {t(`participant.importUsers.para3`)}
              &nbsp;
              <Link target="_blank" href={HelpArticleURL} underline="none">
                {t(`participant.importUsers.helpArticle`)}
              </Link>
              &nbsp;
              {t(`participant.importUsers.para4`)}
            </Typography>
            <Typography variant="body1" pb={3}>
              {t(`participant.importUsers.fileType`)}
            </Typography>
          </Grid>
          <Grid item xs={12} md={5} />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          sx={theme.ParticipantGrid.importUsers.cancelButton}
          disabled={importing}
        >
          {t(`common.cancel`)}
        </Button>

        <Button component="label" variant="contained" disabled={importing}>
          {importing && (
            <CircularProgress
              size={22}
              sx={{ marginRight: 1, color: 'inherit' }}
            />
          )}
          {importing ? (
            t(`participant.importUsers.importing`)
          ) : (
            <>
              {t(`participant.importUsers.import`)}
              <input type="file" accept=".csv" hidden onChange={handleChange} />
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default importUsersDialog
